"use client";

import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Reveal } from "@/components/ui/Reveal";

export type FaqItem = {
  question: string;
  answer: string;
  id?: string;
};

export function FaqSection({ 
  faqs, 
  title = "Frequently Asked Questions",
  subtitle = "Got Questions?",
  className = "py-16 sm:py-20 bg-white relative"
}: { 
  faqs: FaqItem[];
  title?: string;
  subtitle?: string;
  className?: string;
}) {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const toggleFaq = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  if (!faqs || faqs.length === 0) return null;

  return (
    <section className={className}>
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal className="text-center mb-12">
          {subtitle && (
            <span className="font-accent text-xs uppercase tracking-[0.12em] text-brand-orange-deep font-semibold">
              {subtitle}
            </span>
          )}
          <h2 className="mt-4 font-heading text-4xl sm:text-5xl font-semibold leading-tight text-brand-black">
            {title.split(" ").map((word, i, arr) => {
              if (i === arr.length - 1) {
                return <span key={i} className="text-brand-orange">{word}</span>;
              }
              return <span key={i}>{word} </span>;
            })}
          </h2>
        </Reveal>

        <div className="space-y-4">
          {faqs.map((faq, index) => {
            const isOpen = openIndex === index;

            return (
              <Reveal key={faq.id || index} delay={index * 0.1}>
                <div 
                  className={`border rounded-2xl overflow-hidden transition-all duration-300 ${
                    isOpen 
                      ? "bg-white shadow-xl shadow-brand-orange/5 border-brand-orange/30" 
                      : "bg-white border-brand-gray/15 hover:border-brand-orange/30 hover:shadow-md"
                  }`}
                >
                  <button
                    onClick={() => toggleFaq(index)}
                    className="w-full flex items-center justify-between p-5 sm:p-6 text-left focus:outline-none group"
                    aria-expanded={isOpen}
                  >
                    <span className={`text-[17px] sm:text-lg font-heading font-semibold pr-4 transition-colors duration-300 ${isOpen ? "text-brand-orange" : "text-brand-black group-hover:text-brand-orange"}`}>
                      {faq.question}
                    </span>
                    <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${isOpen ? "bg-brand-orange text-white" : "bg-brand-gray-light/80 text-brand-gray group-hover:bg-brand-orange/10 group-hover:text-brand-orange"}`}>
                      {isOpen ? (
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M20 12H4" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
                        </svg>
                      )}
                    </div>
                  </button>
                  <AnimatePresence>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: "easeInOut" }}
                      >
                        <div className="px-5 sm:px-6 pb-5 sm:pb-6 text-[15px] sm:text-base text-brand-gray leading-relaxed">
                          <div className="border-t border-brand-gray/10 pt-4 mt-1">
                            <div className="cms-content" dangerouslySetInnerHTML={{ __html: faq.answer }} />
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}
